import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/vue-query'
import { useAuth } from '@/composables/useAuth.js'
import type {
  ResponseByPath,
  Schemas,
  BodyByPath,
  QueryParamsByPath,
} from '@@/api/types.js'

import type { CamelCasedPropertiesDeep } from '@/utils/types/camelCasePropertiesDeep.js'
import { camelCaseObj } from '@/utils/cases.js'
import {
  useReminderTemplatesListQuery,
  useReminderTemplatesByIdsQuery,
} from '@/composables/reminderTemplates.js'
import { z } from 'zod'
import { useApi } from '@/composables/useApi.js'
import { capitalize as _capitalize, isNil as _isNil } from 'lodash-es'
import type { StronglyTypedUseTranslation } from '@/composables/useTranslation.js'

export type CreationRulesTranslation =
  StronglyTypedUseTranslation<'pages.organization.reminders.creationRules'>

const API_REMINDER_CREATION_RULES_LIST_URL =
  '/{provet_id}/api/reminder_creation_rules'
const API_REMINDER_CREATION_RULES_DETAILS_URL =
  '/{provet_id}/api/reminder_creation_rules/{id}'

export type ReminderCreationRuleByIdResponse = ResponseByPath<
  'get',
  typeof API_REMINDER_CREATION_RULES_DETAILS_URL
>

export type ReminderCreationRuleListResponse = ResponseByPath<
  'get',
  typeof API_REMINDER_CREATION_RULES_LIST_URL
>

export type ReminderCreationRuleUpdateBody = BodyByPath<
  'patch',
  typeof API_REMINDER_CREATION_RULES_DETAILS_URL
>

export type ReminderCreationRuleCreateBody = BodyByPath<
  'post',
  typeof API_REMINDER_CREATION_RULES_LIST_URL
>

type UpdateBodyAttributes = NonNullable<
  ReminderCreationRuleUpdateBody['data']['attributes']
>

type CreateBodyAttributes = NonNullable<
  ReminderCreationRuleCreateBody['data']['attributes']
>

type SchemaCondition = Schemas['CreationRuleCondition']
type SchemaTiming = Schemas['CreationRuleTiming']
type SchemaReminderCreationRule = Schemas['ReminderCreationRule']
type SchemaAttributes = NonNullable<SchemaReminderCreationRule['attributes']>

export type ChoiceOption = {
  readonly value: number
  readonly displayName: string
}

/** Raw API shape (snake_case); use CamelCasedPropertiesDeep after camelCaseObj(). */
export type FieldMetadata = {
  readonly type: string
  readonly required: boolean
  readonly read_only: boolean
  readonly label: string
  readonly choices?: Array<{ value: number; display_name: string }>
  readonly child?: {
    readonly children?: Record<string, FieldMetadata>
  }
}

export type ReminderCreationRuleOptionsResponse = {
  readonly data: {
    readonly actions: {
      readonly POST: Record<string, FieldMetadata>
    }
  }
}

export type NormalizedChoices = {
  readonly triggerIn: ChoiceOption[]
  readonly triggerType: ChoiceOption[]
  readonly applyType: ChoiceOption[]
  readonly recurringType: ChoiceOption[]
  readonly condition: ChoiceOption[]
  readonly comparison: ChoiceOption[]
  readonly sendMethod: ChoiceOption[]
}

export type TemplateOption = {
  value: string
  label: string
  hasEmail: boolean
  hasSms: boolean
  hasPost: boolean
}

/**
 * Trigger workflow: which workflow triggers the reminder creation rule.
 * Values match BE; prefer requesting semantic values from the backend.
 */
export const TriggerIn = {
  ConsultationsAndCounterSales: 0,
  Consultations: 1,
  CounterSales: 2,
  PatientCreation: 3,
} as const

export type TriggerInValue = (typeof TriggerIn)[keyof typeof TriggerIn]

/**
 * Trigger event: which event triggers the reminder creation rule.
 * Values match BE; prefer requesting semantic values from the backend.
 */
export const TriggerType = {
  ItemAdded: 1,
  InvoiceFinalized: 2,
  PatientCreated: 3,
} as const

export type TriggerTypeValue = (typeof TriggerType)[keyof typeof TriggerType]

/**
 * Condition logic: how conditions are combined (every vs at least one).
 * Values match BE; prefer requesting semantic values from the backend.
 */
export const ConditionApplyType = {
  EveryCondition: 1,
  AtLeastOneCondition: 2,
} as const

export type ConditionApplyTypeValue =
  (typeof ConditionApplyType)[keyof typeof ConditionApplyType]

/**
 * Pause/cancel action type: pause only or pause and cancel.
 * Values match BE; prefer requesting semantic values from the backend.
 */
export const PauseCancelActionType = {
  Pause: 1,
  PauseAndCancel: 2,
} as const

export type PauseCancelActionTypeValue =
  (typeof PauseCancelActionType)[keyof typeof PauseCancelActionType]

/**
 * Pause/cancel condition type: which entity triggers the pause/cancel.
 * Values match BE; prefer requesting semantic values from the backend.
 */
export const PauseCancelConditionType = {
  ConsultationReason: 1,
  ConsultationReasonGroup: 2,
  Item: 3,
} as const

export type PauseCancelConditionTypeValue =
  (typeof PauseCancelConditionType)[keyof typeof PauseCancelConditionType]

export const getChoiceLabel = (option: ChoiceOption): string =>
  _capitalize(option.displayName)

/**
 * Gets a label for a value from a list of choice options
 */
export const getLabelForValue = (
  choices: ChoiceOption[],
  value: number,
): string => {
  const option = choices.find((c) => c.value === value)
  return option ? getChoiceLabel(option) : ''
}

/** Recurring unit label for options (days/weeks/months/years). */
export const getRecurringUnitLabel = (
  options: NormalizedChoices | undefined,
  type: number | null,
  t: CreationRulesTranslation,
): string => {
  if (type === null || !options) {
    return ''
  }
  const option = options.recurringType.find((o) => o.value === type)
  if (!option) {
    return ''
  }
  const displayName = option.displayName.toLowerCase()
  const unitMap: Record<string, string> = {
    daily: t('fields.recurringUnits.days'),
    weekly: t('fields.recurringUnits.weeks'),
    monthly: t('fields.recurringUnits.months'),
    yearly: t('fields.recurringUnits.years'),
  }
  return unitMap[displayName] ?? ''
}

/** Template display name from id; use placeholder when empty. */
export const getTemplateName = (
  templateId: string | null,
  templateOptions: Array<{ value: string; label: string }>,
  placeholder = '—',
): string => {
  if (!templateId) {
    return placeholder
  }
  const template = templateOptions.find((t) => t.value === templateId)
  return template?.label ?? placeholder
}

/** Shared delivery validation for reminder send method and template (used by Create and Edit delivery cards). */
export const isReminderSendMethodValid = (r: {
  sendMethod: number | null
}): boolean => r.sendMethod !== null

export const isReminderTemplateValid = (r: {
  template: string | null
}): boolean =>
  r.template !== null && r.template !== '' && String(r.template) !== '0'

export const isReminderValid = (r: {
  sendMethod: number | null
  template: string | null
}): boolean => isReminderSendMethodValid(r) && isReminderTemplateValid(r)

export const getReminderSendMethodError = (
  showErrors: boolean,
  r: { sendMethod: number | null },
  t: CreationRulesTranslation,
): string =>
  showErrors && !isReminderSendMethodValid(r)
    ? t('fields.validation.sendMethodRequired')
    : ''

export const getReminderTemplateError = (
  showErrors: boolean,
  r: { template: string | null },
  t: CreationRulesTranslation,
): string =>
  showErrors && !isReminderTemplateValid(r)
    ? t('fields.validation.templateRequired')
    : ''

/** Reminder type needed for send method and template error getters. */
type ReminderForDeliveryValidation = {
  sendMethod: number | null
  template: string | null
}

/**
 * Maps SendMethodEnum values to template content types.
 * Values from schema: 0 = Email and SMS, 1 = Email, 2 = SMS, 3 = Mail/Post, 4 = External
 */
const SEND_METHOD_CONTENT_TYPE: Record<number, 'email' | 'sms' | 'post'> = {
  0: 'email', // Email and SMS — checked as email
  1: 'email',
  2: 'sms',
  3: 'post',
}

export function getSendMethodContentType(
  sendMethodValue: number,
): 'email' | 'sms' | 'post' | null {
  return SEND_METHOD_CONTENT_TYPE[sendMethodValue] ?? null
}

/**
 * Checks whether a template has content matching the selected send method.
 * Returns the error message if there's a mismatch, or empty string if valid.
 */
export function getTemplateContentMismatchError(
  sendMethod: number | null,
  templateId: string | null,
  sendMethodOptions: ChoiceOption[],
  templateOptions: TemplateOption[],
  t: CreationRulesTranslation,
): string {
  if (sendMethod === null || !templateId) {
    return ''
  }

  const contentType = getSendMethodContentType(sendMethod)
  if (!contentType) {
    return ''
  }

  const template = templateOptions.find((tpl) => tpl.value === templateId)
  if (!template) {
    return ''
  }

  const hasContent =
    (contentType === 'email' && template.hasEmail) ||
    (contentType === 'sms' && template.hasSms) ||
    (contentType === 'post' && template.hasPost)

  if (hasContent) {
    return ''
  }

  const option = sendMethodOptions.find((o) => o.value === sendMethod)
  return t('fields.validation.templateContentMismatch', {
    method: option?.displayName ?? contentType,
  })
}

/**
 * Shared validation state and error getters for delivery cards (Create and Edit).
 * Use in ReminderRuleDeliveryCardCreate and ReminderRuleDeliveryCard.
 */
export function useReminderDeliveryValidation(t: CreationRulesTranslation) {
  const showValidationErrors = ref(false)

  const getSendMethodError = (
    reminder: ReminderForDeliveryValidation,
  ): string =>
    getReminderSendMethodError(showValidationErrors.value, reminder, t)

  const getTemplateError = (reminder: ReminderForDeliveryValidation): string =>
    getReminderTemplateError(showValidationErrors.value, reminder, t)

  return { showValidationErrors, getSendMethodError, getTemplateError }
}

/** Reminder shape required to build delivery form values (timings payload). */
export type ReminderForDeliveryValues = {
  sendMethod: number | null
  template: string | null
  daysToDeadline: number
  isBefore: boolean
}

/** Return type of delivery card getValues() (Create and Edit). */
export type DeliveryFormValues = {
  expiryDays: number | null
  recurringType: number | null
  recurringInterval: number | undefined
  recurringTimes: number | undefined
  timings: Array<{
    sendMethod: number
    template: string
    daysToDeadline: number
    isBefore: boolean
  }>
}

/** Recurring state for buildDeliveryFormValues. */
export type DeliveryRecurringState = {
  enabled: boolean
  type: number | null
  interval: number
  times: number
}

/**
 * Build delivery form values (shared by Create and Edit cards).
 * Call with expiryDays from form (Create: ?? null, Edit: ?? props.expiryDays).
 */
export function buildDeliveryFormValues(
  reminders: ReminderForDeliveryValues[],
  isReminderValidFn: (r: ReminderForDeliveryValues) => boolean,
  expiryDays: number | null,
  recurring: DeliveryRecurringState,
): DeliveryFormValues {
  const timings = reminders.filter(isReminderValidFn).map((r) => ({
    sendMethod: r.sendMethod as number,
    template: r.template as string,
    daysToDeadline: r.daysToDeadline,
    isBefore: r.isBefore,
  }))
  return {
    expiryDays,
    recurringType: recurring.enabled ? recurring.type : null,
    recurringInterval: recurring.enabled ? recurring.interval : undefined,
    recurringTimes: recurring.enabled ? recurring.times : undefined,
    timings,
  }
}

/** Options for createDeliveryValidate. */
export type DeliveryValidateOptions = {
  validateDeliveryForm: () => Promise<{ valid: boolean }>
  hasAtLeastOneValidReminder: () => boolean
  validateReminderDays: () => boolean
  getReminders: () => ReminderForDeliveryValues[]
  isReminderValid: (r: ReminderForDeliveryValues) => boolean
  showValidationErrors: Ref<boolean>
}

/**
 * Returns the shared validate() implementation for delivery cards.
 */
export function createDeliveryValidate(
  options: DeliveryValidateOptions,
): () => Promise<boolean> {
  const {
    validateDeliveryForm,
    hasAtLeastOneValidReminder,
    validateReminderDays,
    getReminders,
    isReminderValid,
    showValidationErrors,
  } = options

  return async (): Promise<boolean> => {
    const expiryResult = await validateDeliveryForm()
    if (!expiryResult.valid) {
      showValidationErrors.value = true
      return false
    }
    if (!hasAtLeastOneValidReminder()) {
      showValidationErrors.value = true
      return false
    }
    if (!validateReminderDays()) {
      showValidationErrors.value = true
      return false
    }
    const reminders = getReminders()
    const allValid = reminders.every(isReminderValid)
    showValidationErrors.value = !allValid
    return allValid
  }
}

/**
 * Returns handleSendMethodChange for delivery cards (clears validation on change).
 */
export function createSendMethodChangeHandler(
  editableReminders: Ref<Array<{ sendMethod: number | null }>>,
  showValidationErrors: Ref<boolean>,
): (index: number, value: number | null) => void {
  return (index: number, value: number | null) => {
    editableReminders.value[index].sendMethod = value
    showValidationErrors.value = false
  }
}

/**
 * Returns handleTemplateChange for delivery cards (clears validation on change).
 */
export function createTemplateChangeHandler(
  editableReminders: Ref<Array<{ template: string | null }>>,
  showValidationErrors: Ref<boolean>,
): (index: number, value: string | null) => void {
  return (index: number, value: string | null) => {
    editableReminders.value[index].template = value
    showValidationErrors.value = false
  }
}

export const isValidNum = (n: unknown): n is number =>
  typeof n === 'number' && !Number.isNaN(n)

/** Shared: validate that every reminder has valid days and "before" days < expiry. */
export const validateReminderDays = (
  reminders: Array<{ daysToDeadline: number; isBefore: boolean }>,
  expiry: number | null | undefined,
): boolean =>
  reminders.every((r) => {
    const days = r.daysToDeadline
    return (
      isValidNum(days) && (!r.isBefore || !isValidNum(expiry) || days < expiry)
    )
  })

/** Shared: error message for reminder days (empty, or enterNumberDays, or mustBeLess). */
export const getReminderDaysError = (
  showErrors: boolean,
  reminder: { daysToDeadline: number; isBefore: boolean },
  expiry: number | null | undefined,
  t: CreationRulesTranslation,
): string => {
  if (!showErrors) {
    return ''
  }
  const days = reminder.daysToDeadline
  if (!isValidNum(days)) {
    return t('fields.validation.enterNumberDays')
  }
  if (reminder.isBefore && isValidNum(expiry) && days >= expiry) {
    return t('fields.validation.reminderSendTimeMustBeLess')
  }
  return ''
}

// Normalize value to array (API returns `unknown`, can be array or single value)
const normalizeValueToArray = (value: unknown): (number | string)[] => {
  if (Array.isArray(value)) {
    return value
  }
  if (!_isNil(value) && value !== '') {
    return [value as number | string]
  }
  return []
}

export type ReminderCreationRuleCondition = Omit<
  CamelCasedPropertiesDeep<SchemaCondition>,
  'value' | 'valueLabels'
> & {
  value: (number | string)[]
  valueLabels: Array<{ value: number | string; label: string }>
}
export type ReminderCreationRuleTiming = CamelCasedPropertiesDeep<SchemaTiming>

// RelatedRule is a normalized form for resolved relationships (id + name from included resources)
export type RelatedRule = {
  readonly id: string
  readonly name: string
}

// Normalized/flattened rule type derived from schema attributes
export type ReminderCreationRule = CamelCasedPropertiesDeep<
  Omit<
    SchemaAttributes,
    'conditions' | 'creation_rule_timings' | 'pause_cancel_conditions'
  >
> & {
  readonly id: string
  readonly departmentId: string | null
  readonly conditions: ReminderCreationRuleCondition[]
  readonly creationRuleTimings: ReminderCreationRuleTiming[]
  readonly pauseCancelEnabled: boolean
  readonly pauseCancelConditions: PauseCancelCondition[]
  readonly overriddenBy: RelatedRule[]
  readonly overriddenRules: RelatedRule[]
}

/** Form state shape used on the create-rule page (step 1–4). */
export type ReminderCreationRuleFormData = {
  readonly name: string
  readonly triggerIn: number
  readonly triggerType: number
  readonly expiryDays: number | null
  readonly applyType: number
  readonly active: boolean
  readonly conditions: ReadonlyArray<{
    readonly condition: number | null
    readonly comparison: number | null
    readonly value: ReadonlyArray<number | string>
    readonly valueLabels?: ReadonlyArray<{
      value: number | string
      label: string
    }>
  }>
  readonly creationRuleTimings: ReadonlyArray<{
    readonly sendMethod: number | null
    readonly template: string | null
    readonly daysToDeadline: number
    readonly isBefore: boolean
  }>
  readonly recurringType: number | null
  readonly recurringInterval: number
  readonly recurringTimes: number
  readonly pauseCancelEnabled: boolean
  readonly pauseCancelConditions: ReadonlyArray<{
    readonly actionType: number | null
    readonly condition: number | null
    readonly value: ReadonlyArray<number | string>
    readonly valueLabels?: ReadonlyArray<{
      value: number | string
      label: string
    }>
  }>
}

const triggerInSchema = z.union([
  z.literal(TriggerIn.ConsultationsAndCounterSales),
  z.literal(TriggerIn.Consultations),
  z.literal(TriggerIn.CounterSales),
  z.literal(TriggerIn.PatientCreation),
])
const triggerTypeSchema = z.union([
  z.literal(TriggerType.ItemAdded),
  z.literal(TriggerType.InvoiceFinalized),
  z.literal(TriggerType.PatientCreated),
])
const applyTypeSchema = z.union([
  z.literal(ConditionApplyType.EveryCondition),
  z.literal(ConditionApplyType.AtLeastOneCondition),
])
const conditionTypeSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
])
const comparisonTypeSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6),
  z.literal(7),
  z.literal(8),
  z.literal(9),
])
const sendMethodSchema = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
])
const recurringTypeSchema = z
  .union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)])
  .nullable()

const syntheticConditionSchema = z.object({
  id: z.number(),
  condition: conditionTypeSchema,
  comparison: comparisonTypeSchema,
  value: z.array(z.string()),
  valueLabels: z.array(
    z.object({ value: z.union([z.number(), z.string()]), label: z.string() }),
  ),
})

const syntheticTimingSchema = z.object({
  id: z.number(),
  sendMethod: sendMethodSchema,
  isBefore: z.boolean(),
  daysToDeadline: z.number(),
  template: z.number(),
})

const syntheticPauseCancelConditionSchema = z.object({
  id: z.number(),
  actionType: z.union([
    z.literal(PauseCancelActionType.Pause),
    z.literal(PauseCancelActionType.PauseAndCancel),
  ]),
  condition: z.union([
    z.literal(PauseCancelConditionType.ConsultationReason),
    z.literal(PauseCancelConditionType.ConsultationReasonGroup),
    z.literal(PauseCancelConditionType.Item),
  ]),
  value: z.array(z.union([z.number(), z.string()])),
  valueLabels: z.array(
    z.object({ value: z.union([z.number(), z.string()]), label: z.string() }),
  ),
})

const syntheticRuleSchema = z.object({
  id: z.string(),
  name: z.string(),
  active: z.boolean(),
  triggerIn: triggerInSchema,
  triggerType: triggerTypeSchema,
  expiryDays: z.number(),
  applyType: applyTypeSchema,
  departmentId: z.string().nullable(),
  conditions: z.array(syntheticConditionSchema),
  creationRuleTimings: z.array(syntheticTimingSchema),
  recurringType: recurringTypeSchema,
  recurringInterval: z.number(),
  recurringTimes: z.number(),
  pauseCancelEnabled: z.boolean(),
  pauseCancelConditions: z.array(syntheticPauseCancelConditionSchema),
  overriddenBy: z.array(z.object({ id: z.string(), name: z.string() })),
  overriddenRules: z.array(z.object({ id: z.string(), name: z.string() })),
})

/**
 * Builds a synthetic ReminderCreationRule from create-page form state for the
 * Review step (ReminderRuleDetailsCardCreate). Validates at runtime with Zod so
 * unexpected form values surface as errors instead of masking bugs.
 */
export function buildMockReminderCreationRule(
  formData: ReminderCreationRuleFormData,
): ReminderCreationRule {
  const raw = {
    id: '',
    name: formData.name || '',
    active: formData.active ?? true,
    triggerIn: formData.triggerIn ?? TriggerIn.Consultations,
    triggerType: formData.triggerType ?? TriggerType.ItemAdded,
    expiryDays: formData.expiryDays ?? 0,
    applyType: formData.applyType ?? ConditionApplyType.EveryCondition,
    departmentId: null,
    conditions: (formData.conditions ?? []).map((c, i) => ({
      id: i,
      condition: c.condition ?? 1,
      comparison: c.comparison ?? 1,
      value: c.value.map((v) => String(v)),
      valueLabels: (c.valueLabels ?? []).map((vl) => ({
        value: vl.value,
        label: vl.label,
      })),
    })),
    creationRuleTimings: (formData.creationRuleTimings ?? []).map((t, i) => ({
      id: i,
      sendMethod: t.sendMethod ?? 0,
      isBefore: t.isBefore,
      daysToDeadline: t.daysToDeadline,
      template: t.template ? Number(t.template) : 0,
    })),
    recurringType: formData.recurringType ?? null,
    recurringInterval: formData.recurringInterval ?? 1,
    recurringTimes: formData.recurringTimes ?? 1,
    pauseCancelEnabled: formData.pauseCancelEnabled ?? false,
    pauseCancelConditions: (formData.pauseCancelConditions ?? []).map(
      (c, i) => ({
        id: i,
        actionType: c.actionType ?? 1,
        condition: c.condition ?? 1,
        value: c.value.map((v) => String(v)),
        valueLabels: (c.valueLabels ?? []).map((vl) => ({
          value: vl.value,
          label: vl.label,
        })),
      }),
    ),
    overriddenBy: [],
    overriddenRules: [],
  }
  const result = syntheticRuleSchema.safeParse(raw)
  if (!result.success) {
    console.warn(
      '[buildMockReminderCreationRule] Zod validation failed, using raw data:',
      result.error.issues,
    )
    return raw as unknown as ReminderCreationRule
  }
  return result.data as ReminderCreationRule
}

type ReminderCreationRulesQueryParams = QueryParamsByPath<
  'get',
  typeof API_REMINDER_CREATION_RULES_LIST_URL
>

const reminderCreationRuleKeys = {
  all: ['reminderCreationRules'] as const,
  lists: () => [...reminderCreationRuleKeys.all, 'list'] as const,
  list: (query: MaybeRef<ReminderCreationRulesQueryParams>) =>
    [...reminderCreationRuleKeys.lists(), query] as const,
  options: () => [...reminderCreationRuleKeys.all, 'options'] as const,
  detail: (ruleId: MaybeRef<string>) =>
    [...reminderCreationRuleKeys.all, 'detail', ruleId] as const,
}

type ReminderCreationRuleOptionsResponseCamel =
  CamelCasedPropertiesDeep<ReminderCreationRuleOptionsResponse>

export const normalizeOptionsResponse = (
  response: ReminderCreationRuleOptionsResponseCamel,
): NormalizedChoices => {
  const postFields = response.data.actions.post
  const conditionsChildren = postFields.conditions?.child?.children ?? {}
  const timingsChildren = postFields.creationRuleTimings?.child?.children ?? {}

  const mapChoices = (
    choices: Array<{ value: number; displayName: string }> | undefined,
  ): ChoiceOption[] =>
    (choices ?? []).map((c) => ({ value: c.value, displayName: c.displayName }))

  return {
    triggerIn: mapChoices(postFields.triggerIn?.choices),
    triggerType: mapChoices(postFields.triggerType?.choices),
    applyType: mapChoices(postFields.applyType?.choices),
    recurringType: mapChoices(postFields.recurringType?.choices),
    condition: mapChoices(conditionsChildren.condition?.choices),
    comparison: mapChoices(conditionsChildren.comparison?.choices),
    sendMethod: mapChoices(timingsChildren.sendMethod?.choices),
  }
}

export const normalizeCreationRule = (
  response: ReminderCreationRuleByIdResponse,
): ReminderCreationRule => {
  const { data, included = [] } = response
  const attributes = data.attributes

  const ruleNameMap = new Map(
    included
      .filter(
        (item): item is typeof item & { attributes: { name?: string } } =>
          item.type === 'CreationRule' && 'attributes' in item,
      )
      .map((item) => [item.id, item.attributes.name ?? '']),
  )

  const overriddenByData = data.relationships?.overridden_by?.data ?? []
  const overriddenRulesData = data.relationships?.overridden_rules?.data ?? []
  const departmentId = data.relationships?.department?.data?.id ?? null

  return {
    id: data.id,
    departmentId,
    name: attributes?.name || '',
    triggerIn: attributes?.trigger_in ?? TriggerIn.Consultations,
    triggerType: attributes?.trigger_type ?? TriggerType.ItemAdded,
    expiryDays: attributes?.expiry_days ?? 0,
    applyType: attributes?.apply_type ?? ConditionApplyType.EveryCondition,
    active: attributes?.active ?? false,
    recurringType: attributes?.recurring_type ?? null,
    recurringInterval: attributes?.recurring_interval,
    recurringTimes: attributes?.recurring_times,
    conditions: (attributes?.conditions || []).map((condition) => {
      type ValueLabelsType = Array<{ value: number; label: string }>
      const valueLabels =
        (condition as { value_labels?: ValueLabelsType }).value_labels ?? []
      return {
        id: condition.id,
        condition: condition.condition,
        comparison: condition.comparison,
        value: normalizeValueToArray(condition.value),
        valueLabels: valueLabels.map((vl) => ({
          value: vl.value,
          label: vl.label,
        })),
      }
    }),
    pauseCancelEnabled: attributes?.pause_cancel_enabled ?? false,
    pauseCancelConditions: (attributes?.pause_cancel_conditions || []).map(
      (pc) => {
        type PcValueLabelsType = Array<{ value: number; label: string }>
        const pcValueLabels =
          (pc as { value_labels?: PcValueLabelsType }).value_labels ?? []
        return {
          id: pc.id,
          actionType: pc.action_type as PauseCancelActionTypeValue,
          condition: pc.condition as PauseCancelConditionTypeValue,
          value: normalizeValueToArray(pc.value),
          valueLabels: pcValueLabels.map((vl) => ({
            value: vl.value,
            label: vl.label,
          })),
        }
      },
    ),
    creationRuleTimings: (attributes?.creation_rule_timings || []).map(
      (timing) => ({
        id: timing.id,
        sendMethod: timing.send_method,
        isBefore: timing.is_before,
        daysToDeadline: timing.days_to_deadline,
        template: timing.template,
      }),
    ),
    overriddenBy: overriddenByData.map((item) => ({
      id: item.id,
      name: ruleNameMap.get(item.id) || '',
    })),
    overriddenRules: overriddenRulesData.map((item) => ({
      id: item.id,
      name: ruleNameMap.get(item.id) || '',
    })),
  }
}

export function useReminderCreationRuleByIdQuery({
  ruleId,
  enabled = true,
}: {
  ruleId: MaybeRef<string>
  enabled?: MaybeRef<boolean>
}) {
  const api = useApi()
  const route = useRoute()

  const resolvedRuleId = computed(() => unref(ruleId))

  const query = useQuery({
    queryKey: computed(() =>
      reminderCreationRuleKeys.detail(resolvedRuleId.value),
    ),
    queryFn: () =>
      api((client) =>
        client.GET(API_REMINDER_CREATION_RULES_DETAILS_URL, {
          params: {
            path: {
              provet_id: route.params.provetId,
              id: resolvedRuleId.value,
            },
          },
        }),
      ),
    enabled,
    select: (response) => normalizeCreationRule(response),
  })

  return reactive(query)
}

export function useReminderCreationRuleListQuery({
  search = '',
  enabled = true,
}: {
  search?: MaybeRef<string>
  enabled?: MaybeRef<boolean>
} = {}) {
  const api = useApi()
  const route = useRoute()

  const queries = computed<ReminderCreationRulesQueryParams>(() => {
    const searchValue = unref(search)
    return {
      ...(searchValue ? { 'filter[search]': searchValue } : {}),
    }
  })

  const query = useQuery({
    placeholderData: keepPreviousData,
    queryKey: reminderCreationRuleKeys.list(queries),
    queryFn: async () =>
      await api((client) =>
        client.GET(API_REMINDER_CREATION_RULES_LIST_URL, {
          params: {
            path: { provet_id: route.params.provetId },
            query: queries.value,
          },
        }),
      ),
    enabled,
  })

  return reactive(query)
}

/**
 * Fetches the OPTIONS metadata for reminder creation rules.
 * This provides all choice labels dynamically from the API.
 */
export function useReminderCreationRuleOptionsQuery() {
  const api = useApi()
  const route = useRoute()

  const query = useQuery({
    queryKey: computed(() => reminderCreationRuleKeys.options()),
    queryFn: async (): Promise<ReminderCreationRuleOptionsResponse> =>
      await api(
        (client) =>
          (client.OPTIONS as typeof client.GET)(
            API_REMINDER_CREATION_RULES_LIST_URL,
            {
              params: {
                path: { provet_id: route.params.provetId },
              },
            },
          ),
        (response) =>
          response.data as unknown as ReminderCreationRuleOptionsResponse,
      ),
    staleTime: Infinity,
    select: (response) =>
      normalizeOptionsResponse(
        camelCaseObj(response) as ReminderCreationRuleOptionsResponseCamel,
      ),
  })

  return reactive(query)
}

/**
 * Shared queries for create and edit reminder rule pages.
 * Returns optionsQuery, templatesQuery, templateOptions, overridesSearch, rulesListQuery.
 *
 * Pass departmentId to scope the template dropdown to the rule's department.
 * Pass selectedTemplateIds to guarantee currently-selected templates appear in the dropdown
 * even when they fall outside the paginated results.
 */
export function useReminderCreationRulePageQueries({
  departmentId,
  selectedTemplateIds,
  templatesEnabled = true,
}: {
  departmentId?: MaybeRef<string | null | undefined>
  selectedTemplateIds?: MaybeRef<string[]>
  /** Delay the template list query until the department source has resolved (e.g. rule data loaded). */
  templatesEnabled?: MaybeRef<boolean>
} = {}) {
  const overridesSearch = ref('')
  const optionsQuery = useReminderCreationRuleOptionsQuery()
  const rulesListQuery = useReminderCreationRuleListQuery({
    search: overridesSearch,
  })
  const templatesQuery = useReminderTemplatesListQuery({
    departmentId,
    enabled: templatesEnabled,
  })
  const resolvedSelectedTemplateIds = computed(
    () => unref(selectedTemplateIds) ?? [],
  )
  const selectedTemplatesQuery = useReminderTemplatesByIdsQuery(
    resolvedSelectedTemplateIds,
  )

  const templateOptions = computed<TemplateOption[]>(() => {
    const listOptions = templatesQuery.data ?? []
    const existingIds = new Set(listOptions.map((o) => o.value))
    const missingSelected = new Map<string, TemplateOption>()
    for (const r of selectedTemplatesQuery.value ?? []) {
      if (r.data && !existingIds.has(r.data.value)) {
        missingSelected.set(r.data.value, r.data)
      }
    }
    return [...missingSelected.values(), ...listOptions]
  })

  return {
    optionsQuery,
    templatesQuery,
    templateOptions,
    overridesSearch,
    rulesListQuery,
  }
}

export type UpdateConditionData = {
  readonly condition: number
  readonly comparison: number
  readonly value: string[] // Backend requires all values as strings
}

export type UpdateTimingData = {
  readonly sendMethod: number
  readonly template: string | null
  readonly daysToDeadline: number
  readonly isBefore: boolean
}

export type UpdatePauseCancelConditionData = {
  readonly actionType: number
  readonly condition: number
  readonly value: string[]
}

export type UpdateReminderCreationRuleData = {
  readonly name: string
  readonly triggerIn: UpdateBodyAttributes['trigger_in']
  readonly triggerType: UpdateBodyAttributes['trigger_type']
  readonly expiryDays: number
  readonly applyType: UpdateBodyAttributes['apply_type']
  readonly active: boolean
  readonly overriddenRuleIds?: string[]
  readonly conditions?: UpdateConditionData[]
  readonly recurringType?: number | null
  readonly recurringInterval?: number
  readonly recurringTimes?: number
  readonly creationRuleTimings?: UpdateTimingData[]
  readonly pauseCancelEnabled?: boolean
  readonly pauseCancelConditions?: UpdatePauseCancelConditionData[]
}

export type CreateReminderCreationRuleData = {
  readonly name: string
  readonly triggerIn: CreateBodyAttributes['trigger_in']
  readonly triggerType: CreateBodyAttributes['trigger_type']
  readonly expiryDays: number
  readonly applyType: CreateBodyAttributes['apply_type']
  readonly active: boolean
  readonly overriddenRuleIds?: string[]
  readonly conditions?: UpdateConditionData[]
  readonly recurringType?: number | null
  readonly recurringInterval?: number
  readonly recurringTimes?: number
  readonly creationRuleTimings?: UpdateTimingData[]
  readonly pauseCancelEnabled?: boolean
  readonly pauseCancelConditions?: UpdatePauseCancelConditionData[]
}

export function useReminderCreationRuleCreateMutation() {
  const api = useApi()
  const route = useRoute()
  const queryClient = useQueryClient()
  const provetId = route.params.provetId as string
  const { activeDepartment } = useAuth()

  return useMutation({
    mutationFn: (data: CreateReminderCreationRuleData) => {
      const relationships: ReminderCreationRuleCreateBody['data']['relationships'] =
        {}

      if (activeDepartment.value?.id) {
        relationships.department = {
          data: {
            type: 'Department',
            id: activeDepartment.value.id,
          },
        }
      }

      if (data.overriddenRuleIds && data.overriddenRuleIds.length > 0) {
        relationships.overridden_rules = {
          data: data.overriddenRuleIds.map((id) => ({
            id,
            type: 'CreationRule' as const,
          })),
        }
      }

      const requestBody: ReminderCreationRuleCreateBody = {
        data: {
          type: 'CreationRule',
          attributes: {
            name: data.name,
            trigger_in: data.triggerIn,
            trigger_type: data.triggerType,
            expiry_days: data.expiryDays,
            apply_type: data.applyType,
            active: data.active,
            recurring_type:
              data.recurringType as CreateBodyAttributes['recurring_type'],
            recurring_interval: data.recurringInterval,
            recurring_times: data.recurringTimes,
            conditions: data.conditions?.map((c) => ({
              condition: c.condition,
              comparison: c.comparison,
              value: c.value,
            })) as CreateBodyAttributes['conditions'],
            creation_rule_timings: data.creationRuleTimings?.map((t) => ({
              send_method: t.sendMethod,
              template: t.template,
              days_to_deadline: t.daysToDeadline,
              is_before: t.isBefore,
            })) as CreateBodyAttributes['creation_rule_timings'],
            pause_cancel_enabled: data.pauseCancelEnabled,
            pause_cancel_conditions: data.pauseCancelConditions?.map((c) => ({
              action_type: c.actionType,
              condition: c.condition,
              value: c.value,
            })) as CreateBodyAttributes['pause_cancel_conditions'],
          },
          relationships:
            Object.keys(relationships).length > 0 ? relationships : undefined,
        },
      }

      return api((client) =>
        client.POST(API_REMINDER_CREATION_RULES_LIST_URL, {
          params: {
            path: { provet_id: provetId },
          },
          body: requestBody,
        }),
      )
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: reminderCreationRuleKeys.lists(),
      })
    },
  })
}

export function useReminderCreationRuleDeleteMutation() {
  const api = useApi()
  const route = useRoute()
  const queryClient = useQueryClient()
  const provetId = route.params.provetId as string

  return useMutation({
    mutationFn: ({
      ruleId,
      force = false,
    }: {
      ruleId: string
      force?: boolean
    }) => {
      const path = { provet_id: provetId, id: ruleId }

      return api((client) =>
        client.DELETE(API_REMINDER_CREATION_RULES_DETAILS_URL, {
          // TODO: remove cast once the backend OpenAPI schema documents `force`
          // as a query parameter for this endpoint. At that point the schema
          // will have `query?: { force?: boolean }` instead of `query?: never`,
          // and QueryParamsByPath can be used for proper type-safe params.
          params: force
            ? ({ path, query: { force: true } } as { path: typeof path })
            : { path },
        }),
      )
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: reminderCreationRuleKeys.lists(),
      })
    },
  })
}

export function useReminderCreationRuleUpdateMutation(ruleId: string) {
  const api = useApi()
  const route = useRoute()
  const queryClient = useQueryClient()
  const provetId = route.params.provetId as string

  return useMutation({
    mutationFn: (data: UpdateReminderCreationRuleData) => {
      const requestBody: ReminderCreationRuleUpdateBody = {
        data: {
          type: 'CreationRule',
          id: ruleId,
          attributes: {
            name: data.name,
            trigger_in: data.triggerIn,
            trigger_type: data.triggerType,
            expiry_days: data.expiryDays,
            apply_type: data.applyType,
            active: data.active,
            recurring_type:
              data.recurringType as UpdateBodyAttributes['recurring_type'],
            recurring_interval: data.recurringInterval,
            recurring_times: data.recurringTimes,
            conditions: data.conditions?.map((c) => ({
              condition: c.condition,
              comparison: c.comparison,
              value: c.value,
            })) as UpdateBodyAttributes['conditions'],
            creation_rule_timings: data.creationRuleTimings?.map((t) => ({
              send_method: t.sendMethod,
              template: t.template,
              days_to_deadline: t.daysToDeadline,
              is_before: t.isBefore,
            })) as UpdateBodyAttributes['creation_rule_timings'],
            pause_cancel_enabled: data.pauseCancelEnabled ?? false,
            pause_cancel_conditions: data.pauseCancelConditions?.map((c) => ({
              action_type: c.actionType,
              condition: c.condition,
              value: c.value,
            })) as UpdateBodyAttributes['pause_cancel_conditions'],
          },
          relationships: data.overriddenRuleIds
            ? {
                overridden_rules: {
                  data: data.overriddenRuleIds.map((id) => ({
                    id,
                    type: 'CreationRule' as const,
                  })),
                },
              }
            : undefined,
        },
      }

      return api((client) =>
        client.PATCH(API_REMINDER_CREATION_RULES_DETAILS_URL, {
          params: {
            path: { provet_id: provetId, id: ruleId },
          },
          body: requestBody,
        }),
      )
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: reminderCreationRuleKeys.detail(ruleId),
        refetchType: 'none',
      })
    },
  })
}

// Condition type constants (from Django backend)
export const ConditionTypes = {
  Item: 1,
  PatientSpecies: 2,
  PatientAgeInMonths: 3,
  ConsultationDiagnosis: 4,
  AmountOfConsultations: 5,
} as const

export type ConditionType = (typeof ConditionTypes)[keyof typeof ConditionTypes]

/**
 * Comparison type constants. Mirrors pcloud.provet.reminder.codes.creationrule
 * (COMP_IN, COMP_NOT_IN, COMP_EQUAL, … COMP_BETWEEN).
 */
export const ComparisonTypes = {
  In: 1,
  NotIn: 2,
  EqualTo: 3,
  NotEqualTo: 4,
  GreaterThan: 5,
  GreaterThanOrEqual: 6,
  LessThan: 7,
  LessThanOrEqual: 8,
  Between: 9,
} as const

export type ComparisonTypeKey = keyof typeof ComparisonTypes
export type ComparisonType = (typeof ComparisonTypes)[ComparisonTypeKey]

/** In/not in – for Item, Species, Diagnosis. Mirrors backend COMP_TYPES_GROUPS. */
export const COMPARISON_TYPES_GROUPS: ComparisonType[] = [
  ComparisonTypes.In,
  ComparisonTypes.NotIn,
]

/** Equal to, greater than, between, etc. – for Age, Consultation amount. Mirrors backend COMP_TYPES_VALUES. */
export const COMPARISON_TYPES_VALUES: ComparisonType[] = [
  ComparisonTypes.EqualTo,
  ComparisonTypes.NotEqualTo,
  ComparisonTypes.GreaterThan,
  ComparisonTypes.GreaterThanOrEqual,
  ComparisonTypes.LessThan,
  ComparisonTypes.LessThanOrEqual,
  ComparisonTypes.Between,
]

const API_ORGANIZATION_ITEMS_URL = '/{provet_id}/api/organization/items'

type OrganizationItemsQueryParams = QueryParamsByPath<
  'get',
  typeof API_ORGANIZATION_ITEMS_URL
>

const conditionValueKeys = {
  items: (search: MaybeRef<string>) => ['conditionItems', search] as const,
}

export function useConditionItemsQuery({
  search = '',
  enabled = true,
}: {
  search?: MaybeRef<string>
  enabled?: MaybeRef<boolean>
} = {}) {
  const api = useApi()
  const route = useRoute()

  const queryParams = computed<OrganizationItemsQueryParams>(() => ({
    'page[size]': 50,
    'search[name]': unref(search) || undefined,
  }))

  const query = useQuery({
    placeholderData: keepPreviousData,
    queryKey: conditionValueKeys.items(search),
    queryFn: async () =>
      await api((client) =>
        client.GET(API_ORGANIZATION_ITEMS_URL, {
          params: {
            path: { provet_id: route.params.provetId },
            query: queryParams.value,
          },
        }),
      ),
    enabled,
    select: (response) =>
      (response.data ?? []).map((item) => ({
        value: item.id,
        label: item.attributes?.name ?? '',
      })),
  })

  return reactive(query)
}

const API_DIAGNOSES_URL = '/{provet_id}/api/departments/{id}/diagnoses'
const API_CODELIST_DIAGNOSES_URL = '/{provet_id}/api/codelist/diagnoses/v1'

type DiagnosisQueryParams = QueryParamsByPath<'get', typeof API_DIAGNOSES_URL>

const conditionDiagnosisKeys = {
  diagnoses: (
    departmentId: MaybeRef<string | null>,
    search: MaybeRef<string>,
  ) => ['conditionDiagnoses', departmentId, search] as const,
}

export function useConditionDiagnosesQuery({
  departmentId,
  search = '',
  enabled = true,
}: {
  departmentId: MaybeRef<string | null>
  search?: MaybeRef<string>
  enabled?: MaybeRef<boolean>
}) {
  const api = useApi()
  const route = useRoute()

  const queryParams = computed<DiagnosisQueryParams>(() => ({
    'page[size]': 50,
    'filter[search]': unref(search) || undefined,
  }))

  const resolvedDepartmentId = computed(() => unref(departmentId))

  const query = useQuery({
    placeholderData: keepPreviousData,
    queryKey: conditionDiagnosisKeys.diagnoses(departmentId, search),
    queryFn: async () => {
      if (resolvedDepartmentId.value) {
        return await api((client) =>
          client.GET(API_DIAGNOSES_URL, {
            params: {
              path: {
                provet_id: route.params.provetId,
                id: resolvedDepartmentId.value as string,
              },
              query: queryParams.value,
            },
          }),
        )
      }
      return await api((client) =>
        client.GET(API_CODELIST_DIAGNOSES_URL, {
          params: {
            path: { provet_id: route.params.provetId },
            query: queryParams.value,
          },
        }),
      )
    },
    enabled,
    select: (response) =>
      (response.data ?? []).map((diagnosis) => ({
        value: diagnosis.id,
        label: diagnosis.attributes?.label ?? '',
      })),
  })

  return reactive(query)
}

// ─── Condition editing helpers (shared by Create and Edit) ───

export type EditableCondition = {
  readonly id: number | null
  readonly clientId: string
  condition: number | null
  comparison: number | null
  value: (number | string)[]
  valueLabels: Array<{ value: number | string; label: string }>
}

export const mapConditionToEditable = (
  c: ReminderCreationRuleCondition,
  generateId: () => string,
): EditableCondition => ({
  id: c.id,
  clientId: generateId(),
  condition: c.condition as number | null,
  comparison: c.comparison as number | null,
  value: c.value,
  valueLabels:
    (c.valueLabels as Array<{ value: number | string; label: string }>) || [],
})

/** Filter comparison options based on condition type (groups vs values). */
export const getComparisonOptionsForType = (
  conditionType: number | null,
  options: NormalizedChoices | undefined,
): ChoiceOption[] => {
  if (!conditionType || !options?.comparison) {
    return []
  }

  let allowedComparisons: number[]

  switch (conditionType) {
    case ConditionTypes.Item:
    case ConditionTypes.PatientSpecies:
    case ConditionTypes.ConsultationDiagnosis:
      allowedComparisons = COMPARISON_TYPES_GROUPS
      break
    case ConditionTypes.PatientAgeInMonths:
    case ConditionTypes.AmountOfConsultations:
      allowedComparisons = COMPARISON_TYPES_VALUES
      break
    default:
      return options.comparison
  }

  return options.comparison.filter((option) =>
    allowedComparisons.includes(option.value),
  )
}

/** Condition type uses search field (ComboboxMultiple): Item, Species, Diagnosis. */
export const isSearchFieldCondition = (conditionType: number | null): boolean =>
  conditionType === ConditionTypes.Item ||
  conditionType === ConditionTypes.PatientSpecies ||
  conditionType === ConditionTypes.ConsultationDiagnosis

/** Condition type uses text input for numeric values: Age, Consultation amount. */
export const isTextInputCondition = (conditionType: number | null): boolean =>
  conditionType === ConditionTypes.PatientAgeInMonths ||
  conditionType === ConditionTypes.AmountOfConsultations

export type ConditionValueOption = { value: string; label: string }

/** Merge query results with selected values that may not be in current results. */
export const mergeOptionsWithSelected = (
  queryOptions: ConditionValueOption[],
  valueLabels: Array<{ value: number | string; label: string }>,
): ConditionValueOption[] => {
  const existingIds = new Set(queryOptions.map((o) => o.value))
  const missingSelected = valueLabels
    .filter((vl) => !existingIds.has(String(vl.value)))
    .map((vl) => ({ value: String(vl.value), label: vl.label }))
  return [
    ...missingSelected,
    ...queryOptions.map((o) => ({ value: o.value, label: o.label })),
  ]
}

export const isConditionFieldValid = (c: EditableCondition): boolean =>
  c.condition !== null

export const isComparisonFieldValid = (c: EditableCondition): boolean =>
  c.comparison !== null

export const isBetweenRangeFormatValid = (value: string): boolean =>
  /^\d+-\d+$/.test(value)

export const isConditionValueValid = (c: EditableCondition): boolean => {
  if (c.value.length === 0 || c.value.some((v) => _isNil(v) || v === '')) {
    return false
  }
  if (c.comparison === ComparisonTypes.Between) {
    return c.value.every((v) => isBetweenRangeFormatValid(String(v)))
  }
  return true
}

export const getConditionFieldError = (
  showErrors: boolean,
  condition: EditableCondition,
  t: CreationRulesTranslation,
): string =>
  showErrors && !isConditionFieldValid(condition)
    ? t('fields.validation.conditionRequired')
    : ''

export const getComparisonFieldError = (
  showErrors: boolean,
  condition: EditableCondition,
  t: CreationRulesTranslation,
): string =>
  showErrors && !isComparisonFieldValid(condition)
    ? t('fields.validation.comparisonRequired')
    : ''

export const getConditionValueError = (
  showErrors: boolean,
  condition: EditableCondition,
  t: CreationRulesTranslation,
): string => {
  if (!showErrors) {
    return ''
  }
  const isEmpty =
    condition.value.length === 0 ||
    condition.value.some((v) => _isNil(v) || v === '')
  if (isEmpty) {
    return isSearchFieldCondition(condition.condition)
      ? t('fields.validation.valueSelectRequired')
      : t('fields.validation.valueRequired')
  }
  if (
    condition.comparison === ComparisonTypes.Between &&
    !condition.value.every((v) => isBetweenRangeFormatValid(String(v)))
  ) {
    return t('fields.validation.valueBetweenFormat')
  }
  return ''
}

// ─── Pause/Cancel condition types and helpers ───

export interface PauseCancelCondition {
  readonly id: number | null
  actionType: PauseCancelActionTypeValue | null
  condition: PauseCancelConditionTypeValue | null
  value: (number | string)[]
  valueLabels: Array<{ value: number | string; label: string }>
}

export interface EditablePauseCancelCondition extends PauseCancelCondition {
  readonly clientId: string
}

export const getPauseCancelActionTypeLabel = (
  actionType: PauseCancelActionTypeValue | null,
  t: CreationRulesTranslation,
  fallback = '—',
): string => {
  if (actionType === PauseCancelActionType.Pause) {
    return t('fields.pauseCancel.actionType.pause')
  }
  if (actionType === PauseCancelActionType.PauseAndCancel) {
    return t('fields.pauseCancel.actionType.pauseAndCancel')
  }
  return fallback
}

export const getPauseCancelConditionTypeLabel = (
  condition: PauseCancelConditionTypeValue | null,
  t: CreationRulesTranslation,
  fallback = '—',
): string => {
  if (condition === PauseCancelConditionType.ConsultationReason) {
    return t('fields.pauseCancel.condition.consultationReason')
  }
  if (condition === PauseCancelConditionType.ConsultationReasonGroup) {
    return t('fields.pauseCancel.condition.consultationReasonGroup')
  }
  if (condition === PauseCancelConditionType.Item) {
    return t('fields.pauseCancel.condition.item')
  }
  return fallback
}

export const mapPauseCancelConditionToEditable = (
  c: PauseCancelCondition,
  generateId: () => string,
): EditablePauseCancelCondition => ({
  id: c.id,
  clientId: generateId(),
  actionType: c.actionType,
  condition: c.condition,
  value: c.value,
  valueLabels: c.valueLabels,
})

export const isPauseCancelActionTypeValid = (
  c: EditablePauseCancelCondition,
): boolean => c.actionType !== null

export const isPauseCancelConditionTypeValid = (
  c: EditablePauseCancelCondition,
): boolean => c.condition !== null

export const isPauseCancelValueValid = (
  c: EditablePauseCancelCondition,
): boolean => c.value.length > 0

export const getPauseCancelActionTypeError = (
  showErrors: boolean,
  c: EditablePauseCancelCondition,
  t: CreationRulesTranslation,
): string =>
  showErrors && !isPauseCancelActionTypeValid(c)
    ? t('fields.validation.pauseCancelActionTypeRequired')
    : ''

export const getPauseCancelConditionTypeError = (
  showErrors: boolean,
  c: EditablePauseCancelCondition,
  t: CreationRulesTranslation,
): string =>
  showErrors && !isPauseCancelConditionTypeValid(c)
    ? t('fields.validation.pauseCancelConditionRequired')
    : ''

export const getPauseCancelValueError = (
  showErrors: boolean,
  c: EditablePauseCancelCondition,
  t: CreationRulesTranslation,
): string =>
  showErrors && !isPauseCancelValueValid(c)
    ? t('fields.validation.pauseCancelValueRequired')
    : ''

// API queries for pause/cancel condition value loading

const API_COMPLAINT_TYPES_URL = '/{provet_id}/api/complaint_types'
const API_COMPLAINT_TYPE_GROUPS_URL = '/{provet_id}/api/complaint_type_groups'

const pauseCancelValueKeys = {
  complaintTypes: (search: MaybeRef<string>) =>
    ['pauseCancelComplaintTypes', search] as const,
  complaintTypeGroups: (search: MaybeRef<string>) =>
    ['pauseCancelComplaintTypeGroups', search] as const,
}

export function usePauseCancelComplaintTypesQuery({
  enabled = true,
}: {
  enabled?: MaybeRef<boolean>
} = {}) {
  const api = useApi()
  const route = useRoute()

  const query = useQuery({
    placeholderData: keepPreviousData,
    queryKey: pauseCancelValueKeys.complaintTypes(''),
    queryFn: async () =>
      await api((client) =>
        client.GET(API_COMPLAINT_TYPES_URL, {
          params: {
            path: { provet_id: route.params.provetId },
            query: {
              'page[size]': 50,
            },
          },
        }),
      ),
    enabled,
    select: (response) =>
      (response.data ?? []).map((item) => ({
        value: item.id,
        label: item.attributes?.name ?? '',
      })),
  })

  return reactive(query)
}

export function usePauseCancelComplaintTypeGroupsQuery({
  search = '',
  enabled = true,
}: {
  search?: MaybeRef<string>
  enabled?: MaybeRef<boolean>
} = {}) {
  const api = useApi()
  const route = useRoute()

  const query = useQuery({
    placeholderData: keepPreviousData,
    queryKey: pauseCancelValueKeys.complaintTypeGroups(search),
    queryFn: async () =>
      await api((client) =>
        client.GET(API_COMPLAINT_TYPE_GROUPS_URL, {
          params: {
            path: { provet_id: route.params.provetId },
            query: {
              'page[size]': 50,
              'filter[search]': unref(search) || undefined,
            },
          },
        }),
      ),
    enabled,
    select: (response) =>
      (response.data ?? []).map((item) => ({
        value: item.id,
        label: item.attributes?.name ?? '',
      })),
  })

  return reactive(query)
}

// ─── Delivery editing helpers (shared by Create and Edit) ───

export type EditableReminder = {
  readonly id: number | null
  readonly clientId: string
  sendMethod: number | null
  template: string | null
  daysToDeadline: number
  isBefore: boolean
}

export const mapTimingToEditable = (
  timing: ReminderCreationRuleTiming,
  generateId: () => string,
): EditableReminder => ({
  id: timing.id ?? null,
  clientId: generateId(),
  sendMethod: timing.sendMethod ?? null,
  template: timing.template !== null ? String(timing.template) : null,
  daysToDeadline: timing.daysToDeadline ?? 0,
  isBefore: timing.isBefore ?? true,
})
